import React, { useMemo, useState } from "react";

import { ArrowDown, ArrowLeft, ArrowUp, Plus, Trash2 } from "lucide-react";

import { sampleFields, toUserGuide } from "./guides";
import { draftTargets } from "./targets";
import {
  draftParameterValues,
  type ActionGuide,
  type SignalAdminDraft,
  type SignalAdminRegistry,
  type SignalDefinition,
  type TargetRecord,
  TIER_META,
} from "./types";
import Button from "../../Components/Button";
import { templateErrors } from "../../Pages/MyPortfolioAccounts/signalCTAs";
import SignalGuideModal, {
  SignalGuideContent,
} from "../../Pages/MyPortfolioAccounts/SignalGuideModal";

interface GuidesPanelProps {
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  /** The applied config's guides, to mark staged rows. */
  appliedGuides: Record<string, ActionGuide>;
  onDraftChange: (next: SignalAdminDraft) => void;
  /** Definition whose guide is open in the editor ("" = the list). */
  editingId: string;
  onEdit: (definitionId: string) => void;
}

/** Mirrors the backend's limits (_guide_errors). */
const MAX_STEPS = 6;
const LIMITS = {
  title: 100,
  why: 400,
  clears_when: 300,
  stepTitle: 100,
  stepBody: 400,
};

const emptyGuide = (): ActionGuide => ({
  live: false,
  title: "",
  why: "",
  steps: [{ title: "", body: "", target: "" }],
  clears_when: "",
});

/** Same key order the backend stores, so an unchanged guide never shows
 * as a staged change. */
const normalized = (g: ActionGuide): ActionGuide => ({
  live: g.live,
  title: g.title.trim(),
  why: g.why.trim(),
  steps: g.steps.map(s => ({
    title: s.title.trim(),
    body: s.body.trim(),
    target: s.target,
  })),
  clears_when: g.clears_when.trim(),
});

const guideErrors = (
  g: ActionGuide,
  targets: Map<string, TargetRecord>,
  placeholders: string[]
): string[] => {
  const errors: string[] = templateErrors(g.why, placeholders).map(
    e => `“Why”: ${e}`
  );
  if (g.title.trim().length < 3) errors.push("Add a guide title.");
  if (!g.clears_when.trim())
    errors.push(
      "Say what clears the signal — it's the one thing the card can't."
    );
  if (g.title.length > LIMITS.title) errors.push("The title is too long.");
  if (g.why.length > LIMITS.why) errors.push("“Why” is too long.");
  if (g.clears_when.length > LIMITS.clears_when)
    errors.push("“This clears when” is too long.");
  if (g.steps.length < 1 || g.steps.length > MAX_STEPS)
    errors.push(`Use 1 to ${MAX_STEPS} steps.`);
  g.steps.forEach((s, i) => {
    if (s.title.trim().length < 3) errors.push(`Step ${i + 1} needs a title.`);
    if (s.title.length > LIMITS.stepTitle || s.body.length > LIMITS.stepBody)
      errors.push(`Step ${i + 1} is too long.`);
    if (s.target && !targets.get(s.target)?.kind)
      errors.push(`Step ${i + 1} links to a target that can't be opened.`);
  });
  return errors;
};

const labelClass =
  "text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500";
const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] text-gray-900 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";

const GuideEditor: React.FC<{
  definition: SignalDefinition;
  registry: SignalAdminRegistry;
  draft: SignalAdminDraft;
  targets: Map<string, TargetRecord>;
  onDraftChange: (next: SignalAdminDraft) => void;
  onBack: () => void;
}> = ({ definition, registry, draft, targets, onDraftChange, onBack }) => {
  const suggestion = registry.guide_suggestions[definition.id];
  const staged = draft.guides[definition.id];
  const [form, setForm] = useState<ActionGuide>(
    () =>
      staged ??
      (suggestion
        ? {
            live: true,
            ...suggestion,
            steps: suggestion.steps.map(s => ({ ...s })),
          }
        : emptyGuide())
  );
  const [touched, setTouched] = useState(false);
  const errors = guideErrors(form, targets, definition.placeholders);

  const setStep = (
    index: number,
    patch: Partial<ActionGuide["steps"][number]>
  ) =>
    setForm(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
  const moveStep = (index: number, by: number) =>
    setForm(prev => {
      const steps = [...prev.steps];
      const [step] = steps.splice(index, 1);
      steps.splice(index + by, 0, step);
      return { ...prev, steps };
    });

  const linkable = Array.from(targets.values()).filter(
    t => t.kind && t.available
  );
  const ctaLabel =
    draft.ctas[definition.id]?.label ?? definition.cta_effective.label;

  const stage = () => {
    setTouched(true);
    if (errors.length) return;
    onDraftChange({
      ...draft,
      guides: { ...draft.guides, [definition.id]: normalized(form) },
    });
    onBack();
  };

  const remove = () => {
    const next = { ...draft.guides };
    delete next[definition.id];
    onDraftChange({ ...draft, guides: next });
    onBack();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          All guides
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-[13px] font-semibold text-gray-900">
          {definition.title}
        </span>
        <span
          className={`rounded px-1.5 py-0.5 text-[10.5px] font-bold ${TIER_META[definition.tier].className}`}
        >
          {TIER_META[definition.tier].label}
        </span>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,560px)]">
        <div className="flex min-w-0 flex-col gap-3.5">
          <label className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5">
            <span>
              <span className="block text-[13px] font-semibold text-gray-900">
                Show on cards
              </span>
              <span className="text-[11.5px] text-gray-500">
                When on, the card&apos;s “{ctaLabel}” button opens this guide
                after you apply.
              </span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={form.live}
              aria-label="Show this guide on cards"
              onClick={() => setForm(prev => ({ ...prev, live: !prev.live }))}
              className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
                form.live ? "bg-emerald-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                  form.live ? "left-[18px]" : "left-0.5"
                }`}
              />
            </button>
          </label>

          <label className="flex flex-col gap-1">
            <span className={labelClass}>Guide title</span>
            <input
              id="guide-title"
              value={form.title}
              onChange={e =>
                setForm(prev => ({ ...prev, title: e.target.value }))
              }
              placeholder="e.g. Turn warm relationships into a commitment"
              className={inputClass}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className={labelClass}>Why you&apos;re seeing this</span>
            <textarea
              id="guide-why"
              value={form.why}
              onChange={e =>
                setForm(prev => ({ ...prev, why: e.target.value }))
              }
              rows={2}
              className={inputClass}
            />
            <span className="text-[11px] text-gray-500">
              Fill in the account&apos;s values with{" "}
              {definition.placeholders.map((p, i) => (
                <React.Fragment key={p}>
                  {i > 0 && ", "}
                  <code className="rounded bg-gray-100 px-1 font-mono text-[10.5px]">
                    {`{{${p}}}`}
                    {definition.nullable_placeholders.includes(p)
                      ? ` (can be empty → ${p === "al" || p === "rl" ? "“?”" : "“unknown”"})`
                      : ""}
                  </code>
                </React.Fragment>
              ))}
              .
            </span>
          </label>

          <div className="flex flex-col gap-2">
            <span className={labelClass}>Steps</span>
            {form.steps.map((step, index) => (
              <div
                key={index}
                className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3"
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-tint text-[11px] font-bold text-brand-dark">
                    {index + 1}
                  </span>
                  <input
                    id={`guide-step-${index}-title`}
                    value={step.title}
                    onChange={e => setStep(index, { title: e.target.value })}
                    placeholder="What to do"
                    aria-label={`Step ${index + 1} title`}
                    className={inputClass}
                  />
                  <div className="flex shrink-0 gap-0.5">
                    <button
                      type="button"
                      disabled={index === 0}
                      onClick={() => moveStep(index, -1)}
                      aria-label={`Move step ${index + 1} up`}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                    >
                      <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      disabled={index === form.steps.length - 1}
                      onClick={() => moveStep(index, 1)}
                      aria-label={`Move step ${index + 1} down`}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30"
                    >
                      <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    </button>
                    <button
                      type="button"
                      disabled={form.steps.length === 1}
                      onClick={() =>
                        setForm(prev => ({
                          ...prev,
                          steps: prev.steps.filter((_, i) => i !== index),
                        }))
                      }
                      aria-label={`Remove step ${index + 1}`}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  </div>
                </div>
                <textarea
                  id={`guide-step-${index}-body`}
                  value={step.body}
                  onChange={e => setStep(index, { body: e.target.value })}
                  rows={2}
                  placeholder="How to do it (optional)"
                  aria-label={`Step ${index + 1} detail`}
                  className={inputClass}
                />
                <select
                  id={`guide-step-${index}-target`}
                  value={step.target}
                  onChange={e => setStep(index, { target: e.target.value })}
                  aria-label={`Step ${index + 1} link`}
                  className={inputClass}
                >
                  <option value="">No link</option>
                  {step.target && !linkable.some(t => t.id === step.target) && (
                    <option value={step.target}>
                      {targets.get(step.target)?.label ?? step.target} —
                      unavailable
                    </option>
                  )}
                  {linkable.map(t => (
                    <option key={t.id} value={t.id}>
                      Link to {t.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            {form.steps.length < MAX_STEPS && (
              <button
                type="button"
                onClick={() =>
                  setForm(prev => ({
                    ...prev,
                    steps: [...prev.steps, { title: "", body: "", target: "" }],
                  }))
                }
                className="inline-flex items-center gap-1 self-start text-[12.5px] font-semibold text-brand hover:underline"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Add step
              </button>
            )}
          </div>

          <label className="flex flex-col gap-1">
            <span className={labelClass}>This clears when</span>
            <textarea
              id="guide-clears-when"
              value={form.clears_when}
              onChange={e =>
                setForm(prev => ({ ...prev, clears_when: e.target.value }))
              }
              rows={2}
              placeholder="What has to change in ExecBridge for this card to go away"
              className={inputClass}
            />
          </label>

          {touched && errors.length > 0 && (
            <ul className="m-0 list-disc rounded-lg border border-red-200 bg-red-50 py-2 pl-7 pr-3 text-[12px] text-red-700">
              {errors.map(e => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
            <Button variant="primary" onClick={stage}>
              Stage guide
            </Button>
            <Button variant="white" onClick={onBack}>
              Cancel
            </Button>
            {suggestion && (
              <button
                type="button"
                onClick={() =>
                  setForm(prev => ({
                    live: prev.live,
                    ...suggestion,
                    steps: suggestion.steps.map(s => ({ ...s })),
                  }))
                }
                className="text-[12px] font-semibold text-brand hover:underline"
              >
                Reset to ExecBridge default
              </button>
            )}
            {(staged || definition.id in draft.guides) && (
              <button
                type="button"
                onClick={remove}
                className="ml-auto text-[12px] font-semibold text-red-700 hover:underline"
              >
                {suggestion ? "Revert to ExecBridge default" : "Remove guide"}
              </button>
            )}
          </div>
        </div>

        <div className="min-w-0">
          <div className="mb-2 flex items-center justify-between">
            <span className={labelClass}>What users see</span>
            <span className="text-[11px] text-gray-400">
              Example values · links inactive
            </span>
          </div>
          <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
            <SignalGuideContent
              preview
              guide={toUserGuide(form, targets)}
              accountName="Example account"
              signalTitle={definition.title}
              tier={definition.tier}
              fields={{
                ...draftParameterValues(registry, draft),
                ...sampleFields(definition.id),
              }}
              primaryLabel={ctaLabel}
              onClose={() => undefined}
            />
          </div>
          {!form.live && (
            <p className="mt-2 text-[11.5px] text-amber-700">
              “Show on cards” is off — users won&apos;t see this guide yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

type GuideStatus = "live" | "hidden" | "default" | "none";

const STATUS_META: Record<GuideStatus, { label: string; className: string }> = {
  live: { label: "Live", className: "bg-emerald-50 text-emerald-700" },
  hidden: { label: "Saved, not shown", className: "bg-gray-100 text-gray-600" },
  default: {
    label: "ExecBridge default · live",
    className: "bg-emerald-50 text-emerald-700",
  },
  none: { label: "No guide", className: "text-gray-400" },
};

const GuidesPanel: React.FC<GuidesPanelProps> = ({
  registry,
  draft,
  appliedGuides,
  onDraftChange,
  editingId,
  onEdit,
}) => {
  const [preview, setPreview] = useState<string | null>(null);
  const targets = useMemo(
    () => new Map(draftTargets(registry, draft).map(t => [t.id, t])),
    [registry, draft]
  );
  const canonical = registry.definitions.filter(
    d =>
      d.definition_status === "canonical" &&
      d.implementation_status === "implemented"
  );
  /** The admin's staged guide, else the live ExecBridge default. */
  const guideOf = (id: string): ActionGuide | undefined => {
    const fallback = registry.guide_suggestions[id];
    return draft.guides[id] ?? (fallback && { live: true, ...fallback });
  };
  const statusOf = (d: SignalDefinition): GuideStatus => {
    const g = draft.guides[d.id];
    if (g) return g.live ? "live" : "hidden";
    return registry.guide_suggestions[d.id] ? "default" : "none";
  };
  const order: Record<GuideStatus, number> = {
    live: 0,
    default: 1,
    hidden: 2,
    none: 3,
  };
  const rows = [...canonical].sort(
    (a, b) => order[statusOf(a)] - order[statusOf(b)]
  );

  const editing = registry.definitions.find(d => d.id === editingId);
  if (editing) {
    return (
      <GuideEditor
        key={editing.id}
        definition={editing}
        registry={registry}
        draft={draft}
        targets={targets}
        onDraftChange={onDraftChange}
        onBack={() => onEdit("")}
      />
    );
  }

  const previewDef = registry.definitions.find(d => d.id === preview);
  const previewGuide = preview ? guideOf(preview) : undefined;

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-[15px] font-bold text-gray-900">Action Guides</h3>
        <p className="mt-0.5 max-w-3xl text-[12.5px] text-gray-500">
          A short playbook a card&apos;s button opens when the steps won&apos;t
          fit on the button — what to do, and what makes the card go away. Edit
          a guide, check the preview, then review and apply to publish it to
          every organization. ExecBridge defaults are live until you publish
          your own version or switch one off.
        </p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full min-w-[760px] border-collapse text-left">
          <thead>
            <tr className="bg-gray-50 text-[10px] font-bold uppercase tracking-[0.07em] text-gray-400">
              <th className="px-4 py-2">Signal</th>
              <th className="px-3 py-2">Button users see</th>
              <th className="px-3 py-2">Guide</th>
              <th className="px-3 py-2">Steps</th>
              <th className="px-3 py-2">Draft</th>
              <th className="px-4 py-2" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-[12.5px]">
            {rows.map(d => {
              const status = statusOf(d);
              const g = draft.guides[d.id];
              const shown = guideOf(d.id);
              const changed =
                JSON.stringify(g ?? null) !==
                JSON.stringify(appliedGuides[d.id] ?? null);
              return (
                <tr key={d.id} className="align-top">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-gray-900">{d.title}</div>
                    <div className="font-mono text-[10.5px] text-gray-400">
                      {d.id}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {draft.ctas[d.id]?.label ?? d.cta_effective.label}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[10.5px] font-bold ${STATUS_META[status].className}`}
                    >
                      {STATUS_META[status].label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-gray-600">
                    {shown ? shown.steps.length : "—"}
                  </td>
                  <td className="px-3 py-2.5">
                    {changed ? (
                      <span className="text-[11px] font-semibold text-amber-700">
                        Unapplied
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      {shown && (
                        <button
                          type="button"
                          onClick={() => setPreview(d.id)}
                          className="rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-600 hover:border-gray-300 hover:text-gray-900"
                        >
                          Preview
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onEdit(d.id)}
                        className="rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-600 hover:border-brand hover:text-brand"
                      >
                        {g
                          ? "Edit"
                          : status === "default"
                            ? "Edit default"
                            : "Create guide"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {previewDef && previewGuide && (
        <SignalGuideModal
          isOpen
          preview
          onClose={() => setPreview(null)}
          guide={toUserGuide(previewGuide, targets)}
          accountName="Example account"
          signalTitle={previewDef.title}
          tier={previewDef.tier}
          fields={{
            ...draftParameterValues(registry, draft),
            ...sampleFields(previewDef.id),
          }}
          primaryLabel={
            draft.ctas[previewDef.id]?.label ?? previewDef.cta_effective.label
          }
        />
      )}
    </div>
  );
};

export default GuidesPanel;
